import React, { useCallback, useRef } from "react";
import {
  Box,
  Command,
  CommandDialog,
  CommandDialogPopup,
  CommandInput,
  CommandList,
  Drawer,
  DrawerHeader,
  DrawerPopup,
  DrawerTitle,
  useIsMobile,
} from "@salestrak/ui";

import { FeedbackDialog } from "#app/components/navigation/feedback-dialog";
import {
  useGlobalSearchOpen,
  useGlobalSearchActions,
} from "./global-search-context";
import { useGlobalSearchContent } from "./use-global-search-content";
import { GlobalSearchFooter } from "./global-search-footer";

// Fraction of the viewport the mobile drawer settles at.
const SNAP_POINTS = [0.92];

/**
 * Global search as a drawer (mobile) or centered command dialog (desktop
 * fallback). Desktop search now lives inline in the header (GlobalSearchBar);
 * this stays mounted for the mobile drawer and as the desktop fallback when
 * something calls `openSearch()`. Both surfaces share the result/command
 * rendering via `useGlobalSearchContent` — the single source of truth.
 */
export function GlobalSearchDialog() {
  const open = useGlobalSearchOpen();
  const { closeSearch } = useGlobalSearchActions();
  const isMobile = useIsMobile();
  const inputRef = useRef<HTMLInputElement>(null);

  const onOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) closeSearch();
    },
    [closeSearch],
  );

  // Focus the input only once the drawer finishes its open animation. Focusing
  // mid-animation opens the virtual keyboard, which resizes the visual viewport
  // and desyncs the drawer's translate math.
  const onOpenChangeComplete = useCallback((isOpen: boolean) => {
    if (isOpen) inputRef.current?.focus();
  }, []);

  const focusInput = useCallback(() => {
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  const {
    query,
    setQuery,
    itemsContent,
    scopeBar,
    footerContext,
    onItemHighlighted,
    listRef,
    feedbackOpen,
    setFeedbackOpen,
  } = useGlobalSearchContent({ onClose: closeSearch, focusInput });

  const command = (
    <Command
      autoHighlight={false}
      mode="none"
      onItemHighlighted={onItemHighlighted}
      onValueChange={setQuery}
      value={query}
    >
      <CommandInput
        ref={inputRef}
        autoFocus={!isMobile}
        placeholder="Search or run a command..."
      />
      {scopeBar}
      {/* grow + min-height:0 hands the library ScrollArea inside CommandList a
          definite height, so the list scrolls instead of pushing the surface. */}
      <Box direction="column" grow minHeight={0}>
        <CommandList ref={listRef}>{itemsContent}</CommandList>
      </Box>
    </Command>
  );

  return (
    <>
      {isMobile ? (
        <Drawer
          onOpenChange={onOpenChange}
          onOpenChangeComplete={onOpenChangeComplete}
          open={open}
          snapPoints={SNAP_POINTS}
        >
          <DrawerPopup showBar>
            <DrawerHeader>
              <DrawerTitle>Search</DrawerTitle>
            </DrawerHeader>
            {command}
          </DrawerPopup>
        </Drawer>
      ) : (
        <CommandDialog onOpenChange={onOpenChange} open={open}>
          <CommandDialogPopup aria-label="Global search">
            {command}
            <GlobalSearchFooter context={footerContext} />
          </CommandDialogPopup>
        </CommandDialog>
      )}

      {feedbackOpen && (
        <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
      )}
    </>
  );
}
